import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Info, Cpu, CreditCard, ShieldCheck, List } from 'lucide-react';
import { Container } from '../ui/Container';
import { cn } from '../../utils/cn';
import { useTranslation } from 'react-i18next';

type FAQItem = {
  category: string;
  question: string;
  answer: string;
  answerBefore?: string;
  answerAfter?: string;
  hasPartnerLink?: boolean;
};

type CategoryKey = 'all' | 'general' | 'product' | 'subscriptions' | 'security';

const CATEGORY_ICONS: Record<CategoryKey, React.ReactNode> = {
  all: <List className="w-4 h-4" />,
  general: <Info className="w-4 h-4" />,
  product: <Cpu className="w-4 h-4" />,
  subscriptions: <CreditCard className="w-4 h-4" />,
  security: <ShieldCheck className="w-4 h-4" />,
};

const CATEGORY_COLORS: Record<CategoryKey, string> = {
  all: 'bg-primary/10 text-primary border-primary/30',
  general: 'bg-blue-50 text-blue-600 border-blue-300',
  product: 'bg-primary/10 text-primary border-primary/30',
  subscriptions: 'bg-emerald-50 text-emerald-600 border-emerald-300',
  security: 'bg-violet-50 text-violet-600 border-violet-300',
};

const BADGE_COLORS: Record<string, string> = {
  general: 'bg-blue-50 text-blue-600 border border-blue-200',
  product: 'bg-primary/10 text-primary border border-primary/20',
  subscriptions: 'bg-emerald-50 text-emerald-600 border border-emerald-200',
  security: 'bg-violet-50 text-violet-600 border border-violet-200',
};

export function FAQ() {
  const { t, i18n } = useTranslation();
  const faqs = t('faq.items', { returnObjects: true }) as FAQItem[];
  const partnerLinkUrl = t('faq.partnerLink.url');
  const partnerLinkText = t('faq.partnerLink.text');
  const categories = t('faq.categories', { returnObjects: true }) as Record<string, string>;

  const [activeCategory, setActiveCategory] = useState<CategoryKey>('all');
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const categoryOrder: CategoryKey[] = ['all', 'general', 'product', 'subscriptions', 'security'];

  const filtered = faqs.filter(
    (faq) => activeCategory === 'all' || faq.category === activeCategory
  );

  return (
    <section className="relative w-full bg-white py-20 lg:py-28 overflow-hidden border-t border-gray-100">
      {/* Ambient background glows */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 left-1/4 w-96 h-96 rounded-full bg-primary/[0.04] blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 rounded-full bg-orange-400/[0.04] blur-[100px]" />
      </div>

      <Container className="relative z-10">
        {/* Header */}
        <div className="mx-auto max-w-2xl text-center mb-12">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium mb-4">
            <Info className="w-3.5 h-3.5" />
            {t('nav.faq')}
          </span>
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            {t('faq.subtitle')}
          </h2>
          <p className="mt-3 text-gray-500 text-base">
            {t('faq.title')}
          </p>
        </div>

        {/* Category filter tabs */}
        <div className="flex items-center justify-center gap-2 flex-wrap mb-10">
          {categoryOrder.map((key) => (
            <button
              key={key}
              onClick={() => {
                setActiveCategory(key);
                setOpenIndex(null);
              }}
              className={cn(
                'flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium border transition-all duration-200',
                activeCategory === key
                  ? CATEGORY_COLORS[key]
                  : 'text-gray-500 border-gray-200 hover:text-gray-700 hover:border-gray-300 bg-white'
              )}
            >
              {CATEGORY_ICONS[key]}
              {categories[key]}
            </button>
          ))}
        </div>

        {/* FAQ accordion */}
        <div className="mx-auto max-w-3xl space-y-3">
          <AnimatePresence mode="wait">
            {filtered.map((faq, index) => {
              const isOpen = openIndex === index;
              const catKey = (faq.category || 'general') as CategoryKey;

              return (
                <motion.div
                  key={`${faq.category}-${faq.question}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.18, delay: index * 0.04 }}
                >
                  <div
                    className={cn(
                      'rounded-2xl border transition-all duration-200',
                      isOpen
                        ? 'bg-white border-primary/25 shadow-md shadow-primary/[0.06]'
                        : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'
                    )}
                  >
                    {/* Question row */}
                    <button
                      type="button"
                      onClick={() => setOpenIndex(isOpen ? null : index)}
                      className="w-full flex items-start justify-between gap-4 px-6 py-5 text-left"
                      aria-expanded={isOpen}
                    >
                      <div className="flex items-start gap-3 min-w-0">
                        <span
                          className={cn(
                            'mt-0.5 shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                            BADGE_COLORS[catKey]
                          )}
                        >
                          {categories[catKey]}
                        </span>
                        <span
                          className={cn(
                            'text-[15px] font-semibold leading-snug transition-colors',
                            isOpen ? 'text-gray-900' : 'text-gray-800'
                          )}
                        >
                          {faq.question}
                        </span>
                      </div>
                      <span
                        className={cn(
                          'shrink-0 mt-0.5 rounded-full p-1 transition-all duration-200',
                          isOpen
                            ? 'bg-primary/15 text-primary rotate-180'
                            : 'bg-gray-100 text-gray-400'
                        )}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </span>
                    </button>

                    {/* Answer panel */}
                    <AnimatePresence>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.22, ease: 'easeInOut' }}
                          className="overflow-hidden"
                        >
                          <div className="px-6 pb-6">
                            <div
                              className={cn(
                                'border-l-2 pl-4',
                                catKey === 'general' && 'border-blue-400',
                                catKey === 'product' && 'border-primary',
                                catKey === 'subscriptions' && 'border-emerald-400',
                                catKey === 'security' && 'border-violet-400'
                              )}
                            >
                              {faq.hasPartnerLink ? (
                                <p className="text-[15px] leading-7 text-gray-600">
                                  {faq.answerBefore}{' '}
                                  <a
                                    href={partnerLinkUrl}
                                    className="text-primary underline underline-offset-2 hover:text-primary/80 transition-colors"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {partnerLinkText}
                                  </a>
                                  {faq.answerAfter}
                                </p>
                              ) : (
                                <p className="text-[15px] leading-7 text-gray-600">{faq.answer}</p>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

        {/* Bottom CTA */}
        <div className="mt-14 text-center">
          <p className="text-gray-500 text-sm mb-4">
            {i18n.language === 'fr'
              ? "Vous ne trouvez pas la réponse que vous cherchez ?"
              : "Can't find the answer you're looking for?"}
          </p>
          <a
            href="mailto:contact@wanzo.io"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 shadow-sm"
          >
            contact@wanzo.io
          </a>
        </div>
      </Container>
    </section>
  );
}
